import { Alert, ButtonType, useModal } from '@ampol/design-system';
import { ButtonColor, ButtonVariant } from '@ampol/design-system/lib';
import { useDispatch, useSelector } from 'react-redux';
import { getCardDetailRequest } from 'actions/cardActions';
import { RootState } from 'reducers';
import { resetPlasticType } from 'actions/maintenanceActions';
import { useSelectSelectedCard } from 'selectors/selectors';
import EditCardModal from './EditCardModal';
import CardModal from '../CardModal';
import CardModalIcon from '../CardModalIcon';
import CardModalAccount from '../CardModalAccount';
import { isWithinDaysBeforeExpiry } from 'utilities/dateUtils';
import { EXPIRY_DAYS_WARNING_THRESHOLD } from '../../../../../constants';
import Env from 'Env';
import './styles.scss';

export const EDIT_CARD_MODAL_FORM_ID = 'edit-card-modal-form';

interface EditCardModalContentProps {
  onClose: () => void;
  status?: string;
  modalType?: string;
  onEvReissueComplete?: () => void;
}

function EditCardModalContent({ onClose, status, modalType, onEvReissueComplete }: EditCardModalContentProps) {
  const isWithinThreshold = isWithinDaysBeforeExpiry(EXPIRY_DAYS_WARNING_THRESHOLD);
  const messageWithBellIcon = (
    <div className='flex w-full'>
      <img src={'/icons/ic-error.svg'} alt='bell' className='ml-m mr-s' />
      <div>
        <div>Your card is expiring soon.</div>
        <div>
          Please note that embossing details and POS prompts can&apos;t be updated until your renewal card is produced
          and dispatched.
        </div>
      </div>
    </div>
  );

  return (
    <EditCardModal onClose={onClose} status={status} modalType={modalType} onEvReissueComplete={onEvReissueComplete}>
      {isWithinThreshold && (
        <div className='edit-card-sticky'>
          <Alert message={messageWithBellIcon} type={'info'} />
        </div>
      )}
      <CardModalAccount />
      <CardModal.Details isEdit={true} />
      <CardModal.DeliveryAddress />
      <CardModal.UsageOptions isEdit={true} />
      <CardModal.ProductsAndServices />
    </EditCardModal>
  );
}

export default function useEditCardModal(
  status?: string,
  onClose?: () => void,
  modalType?: string,
  onEvReissueComplete?: () => void
) {
  const selectedCard = useSelectSelectedCard();
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;
  const dispatch = useDispatch();
  const userStore = useSelector((state: RootState) => state.userStore);
  const assignedGrandfatherClientNumber: string = userStore.assignedGrandfatherClientNumber ?? '';

  const editCardModal = useModal({
    id: 'edit-card-modal',
    formId: EDIT_CARD_MODAL_FORM_ID,
    color: ButtonColor.Blue,
    subtitle: selectedCard?.cardNumber,
    title: 'Edit Card',
    variant: 'triple',
    onClose: () => {
      dispatch(resetPlasticType());
    },
    content: (
      <EditCardModalContent
        onClose={() => {
          onClose && onClose();
          editCardModal.close();
        }}
        status={status}
        modalType={modalType}
        onEvReissueComplete={
          isEvWay4Enabled && onEvReissueComplete
            ? () => {
                // Close the edit card modal first so the design system isn't holding
                // an open modal when AcknowledgeModal calls modal.open().
                editCardModal.close();
                onEvReissueComplete();
              }
            : undefined
        }
      />
    ),
    actions: [
      {
        label: 'Submit',
        type: ButtonType.Submit,
        variant: ButtonVariant.Primary,
      },
      {
        label: 'Cancel',
        variant: ButtonVariant.Secondary,
        onClick: () => {
          if (modalType && modalType === 'account') {
            window.dataLayer.push({
              event: 'Card_Portal_Account_Card_Detail_Edit_Cancel',
              button_click: true,
            });
          } else {
            const cardNumber: string = selectedCard?.cardNumber ?? '';
            if (cardNumber.length > 0 && assignedGrandfatherClientNumber.length > 0) {
              dispatch(getCardDetailRequest(cardNumber, assignedGrandfatherClientNumber));
            }
            window.dataLayer.push({
              event: 'Card_Portal_Individual_Card_Detail_Edit_Cancel',
              button_click: true,
            });
          }
          editCardModal.close();
        },
      },
    ],
    icon: <CardModalIcon />,
  });

  return editCardModal;
}
